import { defineStore } from "pinia";
import { ref } from "vue";
import { useMouse } from "@vueuse/core";

/**
 * Where the pill sits. `cursor` trails the pointer below-right; `cursor-top` trails it too but sits
 * above, clear of the cursor glyph — the arrow and the not-allowed badge extend downward from the
 * hotspot, so a pill below-right of it gets covered (use this on `interactive-disabled` rows). The
 * other three are anchored to the element that owns the tooltip and need `anchorRect` supplied.
 */
export type TooltipPlacement = "cursor" | "cursor-top" | "top" | "right" | "bottom";

/**
 * Two deliberate looks, so the difference is a documented choice rather than an accident of having had
 * three renderers. `hint` wraps at 320px in regular weight (explanatory notes, chart values); `label`
 * is a single semibold line (navigation and toolbar labels, where wrapping would look broken).
 */
export type TooltipVariant = "hint" | "label";

/** The id on the single rendered pill, for `aria-describedby` on whichever element triggered it. */
export const TOOLTIP_PILL_ID = "tooltip-pill";

/** Plain-object mirror of a DOMRect. DOMRect keeps its values behind prototype getters, so it neither
 *  spreads nor survives Vue's reactive proxy cleanly — copy the six numbers we position from. */
export type TooltipAnchor = { top: number; right: number; bottom: number; left: number; width: number; height: number };

export function toTooltipAnchor(rect: DOMRect): TooltipAnchor {
  return { top: rect.top, right: rect.right, bottom: rect.bottom, left: rect.left, width: rect.width, height: rect.height };
}

export type TooltipShowOptions = {
  placement?: TooltipPlacement;
  variant?: TooltipVariant;
  /** Required for `top` / `right` / `bottom`; ignored for `cursor`. */
  anchorRect?: TooltipAnchor | null;
  /**
   * Skip the safety timeout. Used for focus-triggered tooltips: a keyboard user still reading a hint
   * must not be timed out of it while the element is focused (WCAG 1.4.13, "persistent").
   */
  persistent?: boolean;
};

/** Safety net for a missed mouseleave, so a pointer-triggered tooltip can never stick permanently. */
const SAFETY_TIMEOUT_MS = 5000;

/** How long a deferred hide waits for the next trigger, long enough to cross the gap between toolbar buttons. */
export const HANDOFF_GRACE_MS = 120;

export type TooltipHideOptions = {
  /** Wait for the grace period so a neighbouring trigger can take the pill over instead of re-mounting it. */
  deferred?: boolean;
};

export const useTooltip = defineStore("tooltip", () => {
  const { x: mouseX, y: mouseY } = useMouse();

  const visible = ref(false);
  const text = ref("");
  const placement = ref<TooltipPlacement>("cursor");
  const variant = ref<TooltipVariant>("hint");
  const anchorRect = ref<TooltipAnchor | null>(null);
  // True while the pill moved from one trigger to the next without hiding in between.
  const handedOff = ref(false);
  let safetyTimeout: ReturnType<typeof setTimeout> | null = null;
  let pendingHide: ReturnType<typeof setTimeout> | null = null;

  function clearSafetyTimeout() {
    if (safetyTimeout) {
      clearTimeout(safetyTimeout);
      safetyTimeout = null;
    }
  }

  function clearPendingHide(): boolean {
    if (!pendingHide) return false;
    clearTimeout(pendingHide);
    pendingHide = null;
    return true;
  }

  function showTooltip(newText: string, options?: TooltipShowOptions) {
    clearSafetyTimeout();
    handedOff.value = clearPendingHide() && visible.value;
    text.value = newText;
    placement.value = options?.placement ?? "cursor";
    variant.value = options?.variant ?? "hint";
    anchorRect.value = options?.anchorRect ?? null;
    visible.value = true;
    if (!options?.persistent) safetyTimeout = setTimeout(hideTooltip, SAFETY_TIMEOUT_MS);
  }

  function hideTooltip(options?: TooltipHideOptions) {
    clearPendingHide();
    if (options?.deferred && visible.value) {
      pendingHide = setTimeout(() => hideTooltip(), HANDOFF_GRACE_MS);
      return;
    }
    clearSafetyTimeout();
    visible.value = false;
    handedOff.value = false;
    text.value = "";
    anchorRect.value = null;
  }

  return {
    // state
    visible,
    text,
    placement,
    variant,
    anchorRect,
    handedOff,
    mouseX,
    mouseY,

    // actions
    showTooltip,
    hideTooltip,
  };
});
